import { useState, useMemo } from 'react';
import {
  Typography,
  IconButton,
  EditIcon,
  Plus,
  EllipsisMenu,
  Bin,
  IOption,
} from '@weareopenr/component-library';
import { IExtension, IExtensionInstance } from 'src/api/extensions';
import { Logo } from '../Logo/Logo';
import { InstanceRow } from './InstanceRow';
import { usePermissions } from 'src/hooks';
import { getLogoForAddon } from 'src/utils/getLogo';
import styles from './Extensions.module.scss';

interface IExtensionCardProps {
  extension: IExtension;
  onEditAuth: (extension: IExtension) => void;
  onAddInstance: (extension: IExtension) => void;
  onEditInstance: (extension: IExtension, instance: IExtensionInstance) => void;
  onDeleteInstance: (extension: IExtension, instance: IExtensionInstance) => void;
  onDeleteExtension: (extension: IExtension) => void;
}

export const ExtensionCard = ({
  extension,
  onEditAuth,
  onAddInstance,
  onEditInstance,
  onDeleteInstance,
  onDeleteExtension,
}: IExtensionCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { canEditExtensions, canUninstallExtensions } = usePermissions();

  const addonName = extension.addon.name;

  const sortedInstances = useMemo(
    () =>
      extension.instances.slice().sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      }),
    [extension.instances],
  );

  const authStatus = extension.authentication
    ? `${extension.authentication.type} connected`
    : 'Not configured';

  const menuOptions: IOption[] = [];
  if (canEditExtensions && extension.authentication) {
    menuOptions.push({
      id: 'edit-auth',
      name: 'Edit Authentication',
      onOptionClick: () => onEditAuth(extension),
      icon: EditIcon,
    });
  }
  if (canUninstallExtensions) {
    menuOptions.push({
      id: 'delete',
      name: 'Delete Extension',
      onOptionClick: () => onDeleteExtension(extension),
      icon: Bin,
      hoverColour: 'tomato',
    });
  }

  return (
    <div className={styles['extension-card']}>
      <div className={styles['extension-header']}>
        <button
          type="button"
          className={styles['expand-toggle']}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse instances' : 'Expand instances'}
        >
          <span className={styles['expand-icon']}>{isExpanded ? '▼' : '▶'}</span>
        </button>
        <Logo
          src={getLogoForAddon(addonName)}
          name={addonName}
          className={styles['extension-logo']}
        />
        <div className={styles['extension-info']}>
          <Typography weight="semi-bold">{addonName}</Typography>
          <Typography className={styles['auth-status']}>{authStatus}</Typography>
        </div>
        <div className={styles['extension-actions']}>
          {canEditExtensions && (
            <IconButton
              onClick={() => onAddInstance(extension)}
              aria-label="Add instance"
              icon={Plus}
            />
          )}
          {menuOptions.length > 0 && <EllipsisMenu options={menuOptions} variant="outline" />}
        </div>
      </div>

      {isExpanded && (
        <div className={styles['instances-list']}>
          {sortedInstances.length === 0 ? (
            <div className={styles['empty-instances']}>
              <Typography>No instances yet. Add one to start using {addonName}.</Typography>
            </div>
          ) : (
            sortedInstances.map((instance) => (
              <InstanceRow
                key={instance.id}
                instance={instance}
                onEdit={() => onEditInstance(extension, instance)}
                onDelete={() => onDeleteInstance(extension, instance)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};
